/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../../lib/@waca/core-client/js/core-client/ui/core/View',
	'../../../featuresHelper/DateTimeHelper',
	'../../../../widgets/livewidget/nls/StringResources',
	'moment-timezone',
	'@ba/ba-ui-carbon-toolkit',
	'@ba/ba-ui-common-components/lib/filters',
	'react',
	'react-dom'
], function(View, DateTimeHelper, StringResources, moment, UI_TOOLKIT, UICommon, React, ReactDOM) {

	const { FullDateTimeFilter } = UICommon;
	const { Dialog, Root } = UI_TOOLKIT;

	/**
	 * This class renders non numeric prompt dialog, and handles click events.
	 */
	class DateTimePromptDialog extends View {

		/**
		 * prop-types are defined in:
		 * [NonNumericFilter Component]{@link https://github.ibm.com/BusinessAnalytics/ba-ui-common/blob/master/src/filters/NonNumericFilter.tsx}
		 *
		 */
		constructor(options) {
			super(options);

			this.dashboard = options.dashboard;
			this.logger = this.dashboard.getGlassCoreSvc('.Logger');
			const userPreferences = this.dashboard.getGlassCoreSvc('.UserProfile').preferences;
			this.dataSourceId = options.dataSourceId;
			const dataSource = this.dashboard.getFeature('DataSources').getDataSource(this.dataSourceId);
			this.columnId = options.columnId;
			this.mdColumn = dataSource && dataSource.getMetadataColumn(this.columnId);
			this.name = options.name;
			this.locale = userPreferences.productLocale || 'en';
			this.timeZoneID = userPreferences.timeZoneID || 'Etc/UTC';
			this.dataType = options.dataType;
			this._isRangePrompt = options.isRangePrompt;
			//date/time prompts does not range type yet, we hide them in dialog, but need the initial value.
			//date/time prompts only returns values
			this.rangeType = this._isRangePrompt ? 'between': 'after' ;
			this.values = options.values;
			this.isDirty = this.values ? false : true;
			this._onOk = options.onOk;
			this._onCancel = options.onCancel;
			if (UICommon && UICommon.initGlassContext) {
				options.dashboard.prepareGlassOptions(options);
				UICommon.initGlassContext(options.glassContext);
			}

			this.props = {
				// dialog properties
				title: this.name,
				actionTitle: StringResources.get('promptTitle'),
				fromValue: null,
				toValue: null,
				dataType: this._getDataType(),
				rangeType: this.rangeType,
				filterSelectionType: 'range',
				locale: this.locale,
				timezone: this.timeZoneID,
				invertDisabled: true,
				allowSwitch: false,
				allowIncludeNulls: false,
				loading: false,
				totalValues: 2,
				onChange: this._onChange.bind(this),
				// dialog call backs
				onDone: this._onDone.bind(this),
				onApply: this._onDone.bind(this),
				onClear: this._onClear.bind(this),
				onClose: this._onClose.bind(this)
			};

			this.dialogProps = {
				className: 'date-time-prompts',
				size: 'large',
				width: '520px',
				clickaway: false
			};
		}

		_getDataType() {
			let dataType;

			switch (this.dataType) {
				case 'xsdDate':
					dataType = 'date';
					break;

				case 'xsdDateTime':
					dataType = 'datetime';
					break;

				case 'xsdTime':
					dataType = 'time';
					break;

				default:
					break;
			}
			return dataType;
		}
		open() {
			this.dates = this._getDateTimes();
			this._setProps({
				fromValue: this.dates && this.dates[0] ? this.dates[0] : null,
				toValue:  this.dates && this.dates[1] ? this.dates[1] : null,
				isDirty: this.isDirty,
				loading: false
			});
		}

		_getDateTimes() {
			let dates = [];
			if (this.values) {
				const momentTime = moment.tz(this.values[0].label || this.values[0].d, this.DATE_TIME_FORMAT, this.timezone);
				dates.push(DateTimeHelper.getISOString(momentTime.toDate()));
				if (this.values.length > 1) {
					const momentTime1 = moment.tz(this.values[1].label || this.values[1].d, this.DATE_TIME_FORMAT, this.timezone);
					dates.push(DateTimeHelper.getISOString(momentTime1.toDate()));
					this.isDirty = false;
				}
			} else {
				//Get current date in format of YYYY-MM-DDTHH:mm:ss
				let currentDate = DateTimeHelper.getISOString(new Date());
				dates.push(currentDate);
				if (this._isRangePrompt) {
					dates.push(currentDate);
				}
				this.isDirty = true;
			}

			return dates;
		}

		_close() {
			this.dates = null;
			const containerNode = this._getContainerNode();
			if (containerNode) {
				ReactDOM.unmountComponentAtNode(containerNode);
			}
			this.$el.remove();
			this.$el = null;
			this.closed = true;
			super.remove();
		}

		_getContainerNode() {
			return this.$el && this.$el[0];
		}

		_getValues() {
			let values = [];

			if (this.props.fromValue) {
				values.push(this.props.fromValue);
			}

			if (this._isRangePrompt) {
				values.push(this.props.toValue);
			}

			return values;
		}

		_getISOString(date) {
			//Get date in format of YYYY-MM-DDTHH:mm:ss
			return date.toISOString().replace(/\..*/, '');
		}

		_render() {
			const element = (
				<Dialog
					{...this.dialogProps}
				>
					<Root>
						<FullDateTimeFilter {...this.props}/>
					</Root>
				</Dialog>
			);
			ReactDOM.render(element, this._getContainerNode());
		}

		_onChange(payload) {
			this._setProps({
				isDirty: true,
				fromValue: payload.fromValue,
				toValue: payload.toValue
			});
		}

		_onDone() {
			if (!this.closed) {
				this._onOk(this._getValues());
				this._close();
			}
		}

		_onClear() {
			this._setProps({
				fromValue: this.dates && this.dates[0] ? this.dates[0] : '',
				toValue:  this.dates && this.dates[1] ? this.dates[1] : '',
				isDirty: this.isDirty
			});
		}

		_onClose() {
			this._onCancel();
			this._close();
		}

		/**
		 * Re-render whenever a property is updated.
		 * @param {Object} partialProp
		 */
		_setProps(partialProps) {
			this.props.loading = false;
			Object.assign(this.props, partialProps);
			this._render();
		}
	}

	return DateTimePromptDialog;
});
